import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { searchDocumentation, getCustomers, getProjects } from '../services/api';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Search, 
  FileText, 
  Loader2,
  Building2,
  FolderOpen,
  Filter,
  ArrowRight,
  Calendar,
  Star
} from 'lucide-react';

const SearchPage = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [filters, setFilters] = useState({
    customer_id: 'all',
    project_id: 'all',
    file_type: 'all'
  });

  useEffect(() => {
    fetchFilters();
  }, []);

  const fetchFilters = async () => {
    try {
      const [customersRes, projectsRes] = await Promise.all([
        getCustomers(),
        getProjects()
      ]);
      setCustomers(customersRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Failed to fetch filters');
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    
    setLoading(true);
    setHasSearched(true);
    
    try {
      const searchFilters = {};
      if (filters.customer_id !== 'all') searchFilters.customer_id = filters.customer_id;
      if (filters.project_id !== 'all') searchFilters.project_id = filters.project_id;
      if (filters.file_type !== 'all') searchFilters.file_type = filters.file_type;
      
      const response = await searchDocumentation(query, searchFilters);
      setResults(response.data.results);
    } catch (error) {
      toast.error('Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || 'Unknown';
  };

  const getCustomerName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return 'Unknown';
    const customer = customers.find(c => c.id === project.customer_id);
    return customer?.name || 'Unknown';
  };

  return (
    <Layout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']" data-testid="search-title">
            Search Documentation
          </h1>
          <p className="text-slate-500 mt-1">
            Find answers across all your integration documentation
          </p>
        </div>

        {/* Search Form */}
        <Card className="border-slate-200 mb-6">
          <CardContent className="p-6">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="flex gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    placeholder="Search for documentation, code snippets, SQL queries..."
                    className="pl-12 h-12 text-lg"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    data-testid="search-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="bg-[#2B8FD8] hover:bg-[#2075b3] h-12 px-8"
                  disabled={loading || !query.trim()}
                  data-testid="search-btn"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Search'}
                </Button>
              </div>

              {/* Filters */}
              <div className="flex gap-4">
                <Select 
                  value={filters.customer_id} 
                  onValueChange={(v) => setFilters({ ...filters, customer_id: v, project_id: 'all' })}
                >
                  <SelectTrigger className="w-[200px]">
                    <Building2 className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="All Customers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Customers</SelectItem>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select 
                  value={filters.project_id} 
                  onValueChange={(v) => setFilters({ ...filters, project_id: v })}
                >
                  <SelectTrigger className="w-[200px]">
                    <FolderOpen className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="All Projects" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    {projects
                      .filter(p => filters.customer_id === 'all' || p.customer_id === filters.customer_id)
                      .map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Select 
                  value={filters.file_type} 
                  onValueChange={(v) => setFilters({ ...filters, file_type: v })}
                >
                  <SelectTrigger className="w-[150px]">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="archive">Archive</SelectItem>
                    <SelectItem value="python">Python</SelectItem>
                    <SelectItem value="csharp">C#</SelectItem>
                    <SelectItem value="sql">SQL</SelectItem>
                    <SelectItem value="xml">XML</SelectItem>
                    <SelectItem value="json">JSON</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {hasSearched && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900 font-['Plus_Jakarta_Sans']">
                {loading ? 'Searching...' : `${results.length} results found`}
              </h2>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[#2B8FD8]" />
              </div>
            ) : results.length > 0 ? (
              <div className="space-y-4">
                {results.map((result, idx) => (
                  <Card 
                    key={result.id} 
                    className="border-slate-200 hover:shadow-md transition-shadow hover:border-blue-200"
                    data-testid={`search-result-${idx}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <FileText className="h-5 w-5 text-amber-500" />
                            <Link 
                              to={`/projects/${result.project_id}?doc=${result.id}${result.matched_section ? `#${result.matched_section.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}` : ''}`}
                              className="text-lg font-semibold text-slate-900 hover:text-[#2B8FD8] font-['Plus_Jakarta_Sans']"
                            >
                              {result.title}
                            </Link>
                            <Badge variant="outline" className="text-xs">v{result.version}</Badge>
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                            <span className="flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {getCustomerName(result.project_id)}
                            </span>
                            <span className="flex items-center gap-1">
                              <FolderOpen className="h-3 w-3" />
                              {getProjectName(result.project_id)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(result.created_at).toLocaleDateString()}
                            </span>
                          </div>

                          {result.snippet ? (
                            <div className="mb-3">
                              {result.matched_section && (
                                <span className="text-xs font-medium text-[#2B8FD8] uppercase tracking-wide">
                                  Section: {result.matched_section}
                                </span>
                              )}
                              <p className="text-slate-600 text-sm mt-1 line-clamp-3 bg-slate-50 p-2 rounded border-l-2 border-[#2B8FD8]">
                                {result.snippet}
                              </p>
                            </div>
                          ) : result.ai_summary ? (
                            <p className="text-slate-600 mb-3">{result.ai_summary}</p>
                          ) : null}

                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="bg-slate-100 text-slate-600">
                              {result.file_type}
                            </Badge>
                            {result.tags?.map((tag, tidx) => (
                              <Badge key={tidx} variant="secondary" className="bg-blue-50 text-[#2B8FD8]">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 ml-4">
                          <div className="text-right">
                            <div className="flex items-center gap-1 text-amber-500">
                              <Star className="h-4 w-4 fill-amber-500" />
                              <span className="text-sm font-medium">
                                {result.score <= 1
                                  ? (result.score * 100).toFixed(0)
                                  : Math.min(result.score * 10, 100).toFixed(0)}%
                              </span>
                            </div>
                            <span className="text-xs text-slate-400">relevance</span>
                          </div>
                          <Link to={`/projects/${result.project_id}?doc=${result.id}${result.matched_section ? `#${result.matched_section.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}` : ''}`}>
                            <Button variant="outline" size="sm">
                              <ArrowRight className="h-4 w-4" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-slate-200 border-dashed">
                <CardContent className="py-16 text-center">
                  <Search className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2 font-['Plus_Jakarta_Sans']">
                    No results found
                  </h3>
                  <p className="text-slate-500 max-w-sm mx-auto">
                    Try different keywords or adjust your filters to find what you're looking for
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Initial State */}
        {!hasSearched && (
          <Card className="border-slate-200 border-dashed">
            <CardContent className="py-16 text-center">
              <Search className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2 font-['Plus_Jakarta_Sans']">
                Search Your Documentation
              </h3>
              <p className="text-slate-500 max-w-md mx-auto">
                Enter keywords to search across all your integration documentation. 
                Try searching for error messages, function names, or integration types.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default SearchPage;
